import React, { useEffect } from "react";
import { Card, CardBody, CardHeader, Row, Col, Input, Label, FormGroup, Button, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import { membershipSchema, membershipDefaults } from "../../../../models/membership";
import { MEMBERSHIP_TYPES, DURATION_TYPES } from "../constants";
import PropTypes from "prop-types";
import { OverlayLoader, LoadingButton } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";;

/**
 * MembershipEditor
 * Formulário central para criação/edição de Planos e Memberships (Membership).
 */
const MembershipEditor = ({ membership, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: membershipDefaults,
        validationSchema: membershipSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    useEffect(() => {
        if (membership) {
            formik.resetForm({
                values: {
                    id: membership.id || "",
                    name: membership.name || "",
                    type: membership.type || "recurrent",
                    durationType: membership.durationType || "monthly",
                    duration: membership.duration || 1,
                    value: membership.value || 0,
                    maxInstallments: membership.maxInstallments || 1,
                    description: membership.description || "",
                    status: membership.status || GLOBAL_STATUS.ACTIVE,
                    allowsCancellationByApp: membership.allowsCancellationByApp || false,
                    cancellationFee: membership.cancellationFee || 0,
                    cancellationNoticeDays: membership.cancellationNoticeDays || 30,
                    allowsSuspension: membership.allowsSuspension || false,
                    maxSuspensionDays: membership.maxSuspensionDays || 0,
                    suspensionFee: membership.suspensionFee || 0,
                    enrollmentRequired: membership.enrollmentRequired || false,
                    memberXp: membership.memberXp || 0,
                    employeeXp: membership.employeeXp || 0,
                    order: membership.order || 0,
                    createdAt: membership.createdAt || null,
                    createdBy: membership.createdBy || null
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membership]);

    // Notifica o pai quando o form é editado
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!membership) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-file-document-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione um plano</h5>
                    <p className="text-muted">Escolha um plano na lista ao lado ou crie um novo para gerenciar planos e mensalidades.</p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                <i className="mdi mdi-file-document-multiple-outline font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.name : "Novo Plano / Membership"}</span>
                                {formik.values.id && (
                                    <Badge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                        {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                                    </Badge>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">Gerencie o modelo base deste plano</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formik.values.id && (
                            <Button
                                color="light"
                                className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => onDelete(formik.values.id)}
                                disabled={isSaving}
                                title="Excluir Plano"
                            >
                                <i className="mdi mdi-trash-can-outline font-size-18"></i>
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                            loading={isSaving}
                            loadingText="Salvando..."
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-0 border-top" style={{ backgroundColor: '#f8f9fa' }}>
                <OverlayLoader show={isSaving} label="Salvando..." />

                {/* --- SEÇÃO: INFORMAÇÕES BÁSICAS --- */}
                <div className="p-4 bg-white mb-3 shadow-sm">
                    <h6 className="text-uppercase font-size-12 fw-bold text-primary mb-4 d-flex align-items-center">
                        <i className="mdi mdi-information-outline me-2 font-size-18"></i>
                        Detalhes do Plano
                    </h6>

                    <Row className="g-4 mb-2">
                        <Col md="8">
                            <FormGroup className="mb-0">
                                <Label className="fw-medium text-muted small text-uppercase">Nome do Plano / Título <span className="text-danger">*</span></Label>
                                <Input
                                    name="name"
                                    type="text"
                                    className="form-control form-control-lg border-light shadow-sm"
                                    placeholder="Ex: Mensal Básico, Anual Premium..."
                                    {...formik.getFieldProps("name")}
                                    invalid={formik.touched.name && !!formik.errors.name}
                                />
                                <FormFeedback>{formik.errors.name}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup className="mb-0">
                                <Label className="fw-medium text-muted small text-uppercase">Valor Base (R$)</Label>
                                <Input
                                    name="value"
                                    type="number"
                                    className="form-control form-control-lg border-light shadow-sm fw-bold text-success"
                                    placeholder="0.00"
                                    step="0.01"
                                    {...formik.getFieldProps("value")}
                                    invalid={formik.touched.value && !!formik.errors.value}
                                />
                                <FormFeedback>{formik.errors.value}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup className="mb-0">
                                <Label className="fw-medium text-muted small text-uppercase">Modalidade</Label>
                                <select
                                    className={`form-select border-light shadow-sm ${formik.touched.type && formik.errors.type ? 'is-invalid' : ''}`}
                                    {...formik.getFieldProps("type")}
                                >
                                    {MEMBERSHIP_TYPES.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup className="mb-0">
                                <Label className="fw-medium text-muted small text-uppercase">Duração & Período</Label>
                                <div className="d-flex gap-2">
                                    <Input
                                        name="duration"
                                        type="number"
                                        className="form-control border-light shadow-sm"
                                        style={{ width: '40%' }}
                                        {...formik.getFieldProps("duration")}
                                        invalid={formik.touched.duration && !!formik.errors.duration}
                                    />
                                    <select
                                        className="form-select border-light shadow-sm flex-grow-1"
                                        {...formik.getFieldProps("durationType")}
                                    >
                                        {DURATION_TYPES.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                {formik.touched.duration && formik.errors.duration && <div className="text-danger small mt-1">{formik.errors.duration}</div>}
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup className="mb-0">
                                <Label className="fw-medium text-muted small text-uppercase">Máx. Parcelamentos</Label>
                                <Input
                                    name="maxInstallments"
                                    type="number"
                                    className="form-control border-light shadow-sm"
                                    {...formik.getFieldProps("maxInstallments")}
                                    invalid={formik.touched.maxInstallments && !!formik.errors.maxInstallments}
                                />
                                <FormFeedback>{formik.errors.maxInstallments}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="12">
                            <FormGroup className="mb-0 mt-2">
                                <Label className="fw-medium text-muted small text-uppercase">Descrição do Plano (Pública)</Label>
                                <Input
                                    name="description"
                                    type="textarea"
                                    rows="2"
                                    className="form-control border-light shadow-sm"
                                    placeholder="Breve resumo dos benefícios que o aluno adquire ao comprar..."
                                    {...formik.getFieldProps("description")}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </div>

                {/* --- SEÇÃO: REGRAS E PERMISSÕES --- */}
                <div className="p-4">
                    <Row className="g-4">
                        {/* Coluna Esquerda: Switches Globais e Gamificação */}
                        <Col lg="5">
                            <Card className="border-0 shadow-sm h-100 mb-0">
                                <CardBody>
                                    <h6 className="text-uppercase font-size-12 fw-bold text-muted border-bottom pb-2 mb-3">
                                        <i className="mdi mdi-toggle-switch-outline me-2"></i>Permissões
                                    </h6>

                                    <div className="d-flex flex-column gap-3 mb-4">

                                        <div className="d-flex align-items-center justify-content-between p-2 rounded-0 bg-light-subtle border">
                                            <div>
                                                <Label className="mb-0 fw-bold font-size-13" for="switch-enroll">Exigir Matrícula</Label>
                                                <span className="d-block text-muted font-size-11">Força alocação em Turma</span>
                                            </div>
                                            <div className="form-check form-switch form-switch-md mb-0">
                                                <Input
                                                    type="switch"
                                                    id="switch-enroll"
                                                    checked={formik.values.enrollmentRequired === true}
                                                    onChange={(e) => {
                                                        formik.setFieldValue("enrollmentRequired", e.target.checked);
                                                        onChange(true);
                                                    }}
                                                    className="form-check-input"
                                                />
                                            </div>
                                        </div>


                                        <div className="d-flex align-items-center justify-content-between p-2 rounded-0 bg-light-subtle border">
                                            <div>
                                                <Label className="mb-0 fw-bold font-size-13" for="switch-suspension">Permite Trancamento</Label>
                                                <span className="d-block text-muted font-size-11">Pausas no contrato</span>
                                            </div>
                                            <div className="form-check form-switch form-switch-md mb-0">
                                                <Input
                                                    type="switch"
                                                    id="switch-suspension"
                                                    checked={formik.values.allowsSuspension === true}
                                                    onChange={(e) => {
                                                        formik.setFieldValue("allowsSuspension", e.target.checked);
                                                        onChange(true);
                                                    }}
                                                    className="form-check-input custom-switch-warning"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <h6 className="text-uppercase font-size-12 fw-bold text-info border-bottom pb-2 mb-3 mt-4">
                                        <i className="mdi mdi-star-shooting-outline me-2"></i>Gamificação
                                    </h6>
                                    <Row className="g-2">
                                        <Col md="6">
                                            <FormGroup className="mb-0">
                                                <Label className="fw-medium text-muted font-size-11 mb-1">XP Aluno (+)</Label>
                                                <Input
                                                    name="memberXp" type="number"
                                                    className="form-control form-control-sm border-info-subtle"
                                                    {...formik.getFieldProps("memberXp")}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup className="mb-0">
                                                <Label className="fw-medium text-muted font-size-11 mb-1">XP Vendedor (+)</Label>
                                                <Input
                                                    name="employeeXp" type="number"
                                                    className="form-control form-control-sm border-info-subtle"
                                                    {...formik.getFieldProps("employeeXp")}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Coluna Direita: Regras Financeiras */}
                        <Col lg="7">
                            <Card className="border-0 shadow-sm mb-3">
                                <CardBody>
                                    <h6 className="text-uppercase font-size-12 fw-bold text-danger mb-3 d-flex align-items-center">
                                        <i className="mdi mdi-cancel me-2 font-size-16"></i>
                                        Política de Cancelamento
                                    </h6>
                                    <Row className="g-3">
                                        <Col md="6">
                                            <FormGroup className="mb-0">
                                                <Label className="fw-medium text-muted small">Aviso Prévio Obrigatório</Label>
                                                <div className="input-group input-group-sm">
                                                    <Input
                                                        name="cancellationNoticeDays" type="number"
                                                        className="form-control"
                                                        {...formik.getFieldProps("cancellationNoticeDays")}
                                                    />
                                                    <span className="input-group-text bg-light">Dias</span>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup className="mb-0">
                                                <Label className="fw-medium text-muted small">Multa Gerada</Label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-light">%</span>
                                                    <Input
                                                        name="cancellationFee" type="number"
                                                        className="form-control"
                                                        {...formik.getFieldProps("cancellationFee")}
                                                    />
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm mb-0" style={{ opacity: formik.values.allowsSuspension ? 1 : 0.6 }}>
                                <CardBody>
                                    <h6 className="text-uppercase font-size-12 fw-bold text-warning mb-3 d-flex align-items-center">
                                        <i className="mdi mdi-pause-circle-outline me-2 font-size-16"></i>
                                        Política de Trancamento
                                    </h6>

                                    {!formik.values.allowsSuspension && (
                                        <div className="alert alert-warning py-2 small mb-3">
                                            <i className="mdi mdi-alert-circle-outline me-1"></i>
                                            Ative a permissão de trancamento para as regras funcionarem.
                                        </div>
                                    )}

                                    <Row className="g-3">
                                        <Col md="6">
                                            <FormGroup className="mb-0">
                                                <Label className="fw-medium text-muted small">Limite de Dias</Label>
                                                <div className="input-group input-group-sm">
                                                    <Input
                                                        name="maxSuspensionDays" type="number"
                                                        disabled={!formik.values.allowsSuspension}
                                                        className="form-control"
                                                        {...formik.getFieldProps("maxSuspensionDays")}
                                                    />
                                                    <span className="input-group-text bg-light">Dias Totais</span>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup className="mb-0">
                                                <Label className="fw-medium text-muted small">Taxa por Ação</Label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-light">R$</span>
                                                    <Input
                                                        name="suspensionFee" type="number"
                                                        disabled={!formik.values.allowsSuspension}
                                                        className="form-control"
                                                        {...formik.getFieldProps("suspensionFee")}
                                                    />
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </CardBody>
        </Card>
    );
};

MembershipEditor.propTypes = {
    membership: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default MembershipEditor;
